import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchProductById } from '@/lib/api';
import { useCart } from '@/contexts/CartContext';
import { ProductFromAPI, mapProductFromAPI } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { ShoppingCart, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import ImageHandler from '@/components/ImageHandler';

const ProductDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<ProductFromAPI | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { addItem } = useCart();

  useEffect(() => {
    const getProduct = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await fetchProductById(id as string);
        setProduct(data);
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Erro ao carregar o produto. Tente novamente mais tarde.');
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      getProduct();
    }
  }, [id]);

  const handleAddToCart = () => {
    if (product) {
      try {
        const mappedProduct = mapProductFromAPI(product);
        addItem(mappedProduct, quantity);
        toast.success(`${product.name} adicionado ao carrinho!`);
      } catch (error) {
        console.error('Erro ao adicionar ao carrinho:', error);
        toast.error('Erro ao adicionar produto ao carrinho');
      }
    }
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value > 0) {
      setQuantity(value);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="flex flex-col md:flex-row gap-8">
            <div className="md:w-1/2 h-96 bg-gray-200 rounded"></div>
            <div className="md:w-1/2 space-y-4">
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-24 bg-gray-200 rounded"></div>
              <div className="h-10 bg-gray-200 rounded w-1/3"></div>
              <div className="h-12 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-medium text-red-600 mb-2">
            {error || 'Produto não encontrado'}
          </h2>
          <p className="mb-4">Não foi possível carregar o produto solicitado.</p>
          <Link to="/catalog">
            <Button>Ver todos os produtos</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl">
      <Link to="/catalog" className="inline-flex items-center mb-4 text-sm text-navy-600 hover:text-navy-800 transition-colors">
        <ArrowLeft className="w-4 h-4 mr-1" />
        <span>Voltar para o catálogo</span>
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {/* Imagem do Produto */}
        <div className="md:sticky md:top-36 md:self-start">
          {product.image_url ? (
            <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 shadow-md">
              <ImageHandler 
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover"
                fallback="/placeholder-product.png"
              />
            </div>
          ) : (
            <div className="aspect-square rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 shadow-md">
              <div className="text-center">
                <svg className="w-16 h-16 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-sm">Sem imagem</span>
              </div>
            </div>
          )}
        </div>

        {/* Informações do Produto */}
        <div className="space-y-4">
          {/* Título e Preço */}
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
            
            {/* Badges */}
            <div className="flex flex-wrap gap-2 mb-3">
              {product.is_new && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  🆕 Novo
                </span>
              )}
              {product.is_featured && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  ⭐ Destaque
                </span>
              )}
              {product.discount && product.discount > 0 && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  🔥 -{product.discount}% OFF
                </span>
              )}
            </div>

            {/* Preço */}
            <div className="flex items-baseline gap-3">
              {product.discount && product.discount > 0 ? (
                <>
                  <span className="text-3xl font-bold text-navy-600">
                    {(product.price * (1 - product.discount / 100)).toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </span>
                  <span className="text-lg text-gray-500 line-through">
                    {product.price.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </span>
                </>
              ) : (
                <span className="text-3xl font-bold text-navy-600">
                  {product.price.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </span>
              )}
            </div>
          </div>

          {/* Disponibilidade */}
          <div className="flex items-center gap-2 text-sm">
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${product.stock > 0 ? 'bg-green-500' : 'bg-red-500'}`}></span>
            <span className={product.stock > 0 ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
              {product.stock > 0 ? `Em estoque (${product.stock} disponíveis)` : 'Sem estoque'}
            </span>
          </div>

          <Separator />

          {/* Descrição */}
          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-2">Descrição</h2>
            <p className="text-sm text-gray-700 leading-relaxed">{product.description}</p>
          </div>

          <Separator />

          {/* Quantidade e Adicionar ao Carrinho */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <label htmlFor="quantity" className="text-sm font-medium text-gray-700">
                Quantidade:
              </label>
              <Input
                id="quantity"
                type="number"
                min="1"
                max={product.stock}
                value={quantity}
                onChange={handleQuantityChange}
                className="w-20 text-center"
                disabled={product.stock === 0}
              />
            </div>

            <Button 
              onClick={handleAddToCart}
              className="w-full"
              size="lg"
              disabled={product.stock === 0}
            >
              <ShoppingCart className="mr-2 h-5 w-5" />
              {product.stock > 0 ? 'Adicionar ao Carrinho' : 'Produto Indisponível'}
            </Button>
          </div>

          {/* Informações Adicionais */}
          <Card className="bg-blue-50 border-blue-200 p-4">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-blue-900">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Entrega rápida e segura</span>
              </div>
              <div className="flex items-center gap-2 text-blue-900">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Garantia de qualidade</span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Descrição Detalhada */}
      {product.full_description && (
        <div className="mt-8 max-w-4xl">
          <Separator className="mb-6" />
          <h2 className="text-xl font-bold mb-4 text-gray-900">Descrição Detalhada</h2>
          <div className="prose prose-sm prose-slate max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: product.full_description }} />
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
